import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Radar, ShieldCheck, MessageSquareText } from "lucide-react";
import { Button } from "../ui/button";
import LivePulse from "../LivePulse";
import ScanTape from "./ScanTape";

export function Reveal({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || !("IntersectionObserver" in window)) { setInView(true); return undefined; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); io.disconnect(); } }, { threshold: 0.12 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return <div ref={ref} className={`mb-reveal ${inView ? "is-in" : ""} ${className}`} style={{ transitionDelay: `${delay}ms` }}>{children}</div>;
}

// Full-bleed section with its own background image under a dark overlay.
export function BgSection({ img, id, testId, children, overlay = "from-[#07040f] via-black/70 to-[#07040f]", className = "" }) {
  return (
    <section id={id} className={`relative overflow-hidden ${className}`} data-testid={testId}>
      {img && <img src={img} alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />}
      <div className={`absolute inset-0 bg-gradient-to-b ${overlay}`} />
      <div className="relative max-w-6xl mx-auto px-6 lg:px-12 py-24 sm:py-32 text-center">{children}</div>
    </section>
  );
}

export const Headline = ({ children, className = "", testId }) => (
  <h2 className={`font-serif-hero text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-white ${className}`} data-testid={testId}>{children}</h2>
);

function useHeroVideo() {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const ok = window.matchMedia("(min-width: 768px)").matches && !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    setOn(ok);
  }, []);
  return on;
}

export function Hero() {
  const wantsVideo = useHeroVideo();
  const [videoFailed, setVideoFailed] = useState(false);
  const video = wantsVideo && !videoFailed;
  return (
    <section className="relative min-h-[calc(92vh-3rem)] md:min-h-[92vh] flex items-start md:items-center overflow-hidden" data-testid="home-hero">
      {video ? (
        <video className="absolute inset-0 w-full h-full object-cover" poster="/media/hero.webp?v=lens1" autoPlay muted loop playsInline aria-hidden="true" data-testid="hero-video">
          <source src="/media/hero-loop.webm?v=lens1" type="video/webm" />
          <source src="/media/hero-loop.mp4?v=lens1" type="video/mp4" onError={() => setVideoFailed(true)} />
        </video>
      ) : (
        <>
          <img src="/media/hero.webp?v=lens1" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover hidden md:block" data-testid="hero-still" />
          <div className="absolute inset-x-0 bottom-0 h-[36vh] md:hidden" data-testid="hero-mobile-art">
            <img src="/media/hero-mobile.webp?v=mob5" alt="" aria-hidden="true" className="w-full h-full object-cover object-bottom" data-testid="hero-mobile-still" />
            <div className="absolute inset-x-0 top-0 h-1/4 bg-gradient-to-b from-[#07040f] to-transparent" />
            <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[#07040f] to-transparent" />
          </div>
        </>
      )}
      <div className="absolute inset-0 hidden md:block bg-gradient-to-r from-black/90 via-black/60 to-black/20" />
      <div className="absolute inset-x-0 bottom-0 h-40 hidden md:block bg-gradient-to-t from-[#07040f] to-transparent" />
      {video && <ScanTape />}
      <div className="relative max-w-6xl mx-auto px-6 lg:px-12 pt-[calc(2.5rem+10vh)] pb-[28vh] md:py-24 w-full">
        <div className="max-w-3xl fade-in-up">
          <LivePulse />
          <h1 className="mt-5 md:mt-8 font-serif-hero text-6xl sm:text-7xl lg:text-[7.5rem] leading-[0.95] text-white">
            <span className="block">Skip the latte.</span>
            <span className="block italic text-violet-300">Catch the rocket.</span>
          </h1>
          <p className="mt-5 md:mt-8 text-lg sm:text-xl text-neutral-100 max-w-xl leading-relaxed [text-shadow:0_1px_12px_rgba(0,0,0,0.7)]" data-testid="hero-line">
            The playing field, leveled. Quant-level analysis for busy investors, in plain-English alerts and insights.
          </p>
          <div className="mt-7 md:mt-10 flex flex-col sm:flex-row sm:items-center gap-4 md:gap-5">
            <Link to="/register">
              <Button size="lg" className="bg-mb-emerald hover:bg-mb-emerald-dark text-white rounded-full px-9 h-14 text-base" data-testid="hero-cta-signup">
                Start brewing alerts <ArrowRight className="w-5 h-5 ml-1" />
              </Button>
            </Link>
            <span className="text-neutral-300" data-testid="hero-price">
              <span className="font-serif-hero text-3xl text-white">$4.99</span>/week. Cancel anytime.
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

export function CoffeeMath() {
  return (
    <BgSection img="/media/coffee.webp" testId="home-coffee-math">
      <Reveal>
        <Headline>The coffee math.</Headline>
        <div className="mt-14 grid sm:grid-cols-2 gap-10 sm:gap-16 max-w-3xl mx-auto">
          <div data-testid="coffee-latte">
            <div className="font-serif-hero text-7xl sm:text-8xl text-neutral-400 tabular-nums">$32.25</div>
            <p className="mt-3 text-lg text-neutral-400">one $6.45 latte, every weekday</p>
          </div>
          <div data-testid="coffee-moonbucks">
            <div className="font-serif-hero text-7xl sm:text-8xl text-white tabular-nums">$4.99</div>
            <p className="mt-3 text-lg text-neutral-200">a whole week of Moonbucks</p>
          </div>
        </div>
        <p className="mt-12 text-xl text-neutral-300">Less than <span className="text-white">$1 a day</span> to know what’s moving.</p>
      </Reveal>
    </BgSection>
  );
}

const PILLARS = [
  { key: "screener", icon: Radar, title: "Real-time screener dashboard", body: "Our desk continuously scans the U.S. market, ranks what’s heating up in real time, and surfaces only the setups that clear every filter." },
  { key: "tested", icon: ShieldCheck, title: "Battle-tested edge", body: "Every model is stress-tested against 12+ months of live market conditions before it publishes a single alert." },
  { key: "plain", icon: MessageSquareText, title: "Plain-English alerts", body: "Every alert tells you the story: what’s firing, what the market backdrop looks like, and our conviction level — in a paragraph, not a chart." },
];

export function Pillars() {
  return (
    <section className="max-w-6xl mx-auto px-6 lg:px-12 py-24 sm:py-32 text-center" data-testid="home-pillars">
      <Reveal><Headline>Built for busy investors. Not chart nerds.</Headline></Reveal>
      <Reveal className="mt-14">
        <div className="relative overflow-hidden rounded-2xl border border-white/10 min-h-[420px] flex items-end justify-center" data-testid="pillar-human">
          <img src="/media/human.webp" alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/5" />
          <div className="relative p-7 sm:p-9 max-w-2xl">
            <h3 className="font-serif-hero text-white leading-tight text-4xl sm:text-5xl">Analyst-vetted before publish</h3>
            <p className="mt-3 text-sm sm:text-base text-neutral-300 leading-relaxed">Algorithms surface candidates. Our analyst desk eyeballs every single one before it hits your inbox — so the noisy ones never make the cut.</p>
          </div>
        </div>
      </Reveal>
      <div className="mt-4 grid md:grid-cols-3 gap-4">
        {PILLARS.map((p, i) => (
          <Reveal key={p.key} delay={i * 80}>
            <div className="h-full rounded-2xl border border-white/10 bg-white/[0.02] p-8 flex flex-col items-center transition-colors hover:border-violet-400/30" data-testid={`pillar-${p.key}`}>
              <span className="w-12 h-12 rounded-full border border-violet-400/30 bg-violet-500/10 flex items-center justify-center">
                <p.icon className="w-5 h-5 text-violet-300" strokeWidth={1.75} />
              </span>
              <h3 className="mt-6 font-serif-hero text-white leading-tight text-3xl">{p.title}</h3>
              <p className="mt-3 text-sm sm:text-base text-neutral-400 leading-relaxed">{p.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}
